import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { MagnifyingGlass, ArrowUpRight, Sparkle, CheckCircle, X } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import PaymentMethodPicker from "@/components/PaymentMethodPicker";

const TIER_PERKS = {
  week: ["Sponsored badge across the index", "Email digest mention (1×)", "Permanent share card on social"],
  month: ["Everything in Spotlight", "Homepage carousel placement", "Featured in weekly digest (4×)"],
  quarter: ["Everything in Premium", "Top of Atlas & search results", "Custom social-share image", "13× digest features"],
};

export default function SponsorPage() {
  const [search] = useSearchParams();
  const preselect = search.get("conference_id") || "";
  const canceled = search.get("canceled") === "1";

  const [tiers, setTiers] = useState([]);
  const [provider, setProvider] = useState(null);       // {id, name, currency, currency_symbol, ...}
  const [providerId, setProviderId] = useState("");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(null); // {id, title, ...}
  const [tier, setTier] = useState("month");
  const [busy, setBusy] = useState(false);

  const useSAR = provider?.currency === "SAR";

  useEffect(() => { api.get("/sponsorship/tiers").then((r) => setTiers(r.data)); }, []);

  useEffect(() => {
    if (canceled) toast.info("Checkout canceled — you weren't charged.");
  }, [canceled]);

  // load preselected conference if id in URL
  useEffect(() => {
    if (!preselect) return;
    api.get(`/conferences/${preselect}`).then((r) => setPicked({ id: r.data.id, title: r.data.title, topic: r.data.topic, city: r.data.city, country: r.data.country })).catch(() => {});
  }, [preselect]);

  useEffect(() => {
    if (picked) return;
    const id = setTimeout(() => {
      if (!q.trim()) { setResults([]); return; }
      api.get(`/conferences`, { params: { q, limit: 8 } }).then((r) => setResults(r.data.items || []));
    }, 220);
    return () => clearTimeout(id);
  }, [q, picked]);

  const tierObj = useMemo(() => tiers.find((t) => t.tier === tier), [tiers, tier]);

  function tierPrice(tt) {
    if (!tt) return "—";
    if (useSAR) return `SAR ${(tt.amount_sar ?? tt.amount * 3.75).toFixed(0)}`;
    return `$${tt.amount.toFixed(0)}`;
  }

  async function startCheckout() {
    if (!picked) { toast.error("Pick a conference first"); return; }
    if (!providerId) { toast.error("Pick a payment method"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/sponsorship/checkout", {
        conference_id: picked.id,
        tier,
        origin_url: window.location.origin,
        provider: providerId,
      });
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error("No checkout URL received");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start checkout");
    } finally { setBusy(false); }
  }

  return (
    <div data-testid="sponsor-page" className="px-6 md:px-10 lg:px-16 py-12 md:py-16 max-w-5xl">
      <div className="overline mb-3">Promote · Sponsored slots</div>
      <h1 className="font-display font-black text-5xl md:text-7xl tracking-[-0.04em] leading-[.92] mb-6">
        Get your conference in front of <span className="italic font-medium text-blue-700">thousands of researchers</span>
      </h1>
      <p className="text-zinc-600 text-lg leading-relaxed max-w-3xl">
        Promote your event with an editorial sponsored slot on Symposia — homepage carousel, "Sponsored" badge across the catalog, and a feature in our weekly Topic-of-the-Week digest. Pay once, no subscription.
      </p>

      {/* Step 1: pick conference */}
      <section className="mt-14">
        <div className="overline mb-3">Step 01 · Pick the conference</div>
        {picked ? (
          <div className="border-2 border-black p-5 flex items-start gap-4" data-testid="sponsor-picked">
            <div className="flex-1">
              <div className="font-display font-bold text-xl tracking-tight">{picked.title}</div>
              <div className="text-xs text-zinc-500 font-mono mt-1">{picked.topic} · {picked.city}{picked.country ? `, ${picked.country}` : ""}</div>
            </div>
            <button onClick={() => { setPicked(null); setQ(""); }} data-testid="sponsor-unpick" className="text-xs font-mono uppercase tracking-wider text-zinc-500 hover:text-zinc-950 flex items-center gap-1"><X size={12} /> Change</button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 border-b border-zinc-300 py-2 focus-within:border-zinc-950">
              <MagnifyingGlass size={16} className="text-zinc-400" />
              <input data-testid="sponsor-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, topic, city…" className="flex-1 bg-transparent outline-none text-sm" />
            </div>
            {results.length > 0 && (
              <div className="border border-zinc-200 mt-3 divide-y divide-zinc-200 max-h-[320px] overflow-y-auto">
                {results.map((c, i) => (
                  <button key={c.id} type="button" onClick={() => setPicked(c)} data-testid={`sponsor-result-${i}`} className="w-full text-start px-5 py-3 hover:bg-[#FAFAFA] flex items-center justify-between">
                    <div>
                      <div className="font-display font-semibold text-sm">{c.title}</div>
                      <div className="text-xs text-zinc-500 font-mono mt-0.5">{c.topic} · {c.city}{c.country ? `, ${c.country}` : ""}</div>
                    </div>
                    <ArrowUpRight size={14} data-arrow="forward" className="text-zinc-300" />
                  </button>
                ))}
              </div>
            )}
            <p className="mt-3 text-xs font-mono text-zinc-500">Can't find it? <Link to="mailto:hello@conferencely.org" className="underline">Email us</Link> to add it first.</p>
          </>
        )}
      </section>

      {/* Step 2: pick tier */}
      <section className="mt-14">
        <div className="overline mb-3">Step 02 · Choose a tier</div>
        <div className="grid md:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
          {tiers.map((tt) => {
            const selected = tier === tt.tier;
            return (
              <button
                key={tt.tier}
                onClick={() => setTier(tt.tier)}
                data-testid={`tier-${tt.tier}`}
                type="button"
                className={(selected ? "bg-zinc-950 text-white" : "bg-white hover:bg-[#FAFAFA]") + " p-7 text-start transition-colors relative"}
              >
                <div className={(selected ? "text-blue-300" : "text-blue-700") + " overline mb-2 flex items-center gap-1"}>
                  {tt.tier === "month" && <Sparkle size={11} weight="fill" />} {tt.label}
                </div>
                <div className="font-display font-black text-5xl tracking-tight tabular leading-none">
                  {tierPrice(tt)}
                </div>
                <div className={(selected ? "text-zinc-400" : "text-zinc-500") + " text-xs font-mono mt-1"}>{tt.days} days · {provider?.currency || "USD"} · one-time</div>
                <ul className="mt-5 space-y-1.5 text-sm">
                  {(TIER_PERKS[tt.tier] || []).map((p) => (
                    <li key={p} className="flex items-start gap-2"><CheckCircle size={14} weight="fill" className={(selected ? "text-blue-300" : "text-blue-600") + " mt-0.5 flex-shrink-0"} /> <span>{p}</span></li>
                  ))}
                </ul>
                {selected && <div className="absolute top-3 right-3 text-[10px] font-mono uppercase tracking-wider">selected</div>}
              </button>
            );
          })}
        </div>
      </section>

      {/* Step 3: checkout */}
      <section className="mt-14 border-t border-zinc-950 pt-6">
        <div className="overline mb-3">Step 03 · Pay securely</div>

        <PaymentMethodPicker
          value={providerId}
          onChange={(id, p) => { setProviderId(id); setProvider(p); }}
          dataTestidPrefix="sponsor-pay"
        />

        <div className="grid md:grid-cols-12 gap-6 items-end mt-6">
          <div className="md:col-span-7">
            <p className="text-sm text-zinc-600 max-w-md leading-relaxed">
              {useSAR
                ? "You'll be redirected to a secure Paylink hosted checkout. Pay with your preferred method — we activate the sponsored slot immediately on confirmation."
                : "You'll be redirected to a secure hosted checkout. We activate the sponsored slot immediately on payment confirmation — no waiting."}
            </p>
          </div>
          <div className="md:col-span-5">
            <button
              onClick={startCheckout}
              data-testid="sponsor-checkout-btn"
              disabled={!picked || !providerId || busy}
              className="btn-primary w-full justify-center text-base"
              style={{ padding: "1rem 1.4rem" }}
            >
              {busy ? "Opening checkout…" : `Pay ${tierPrice(tierObj)} · ${tierObj?.label || ""}`}
              {!busy && <ArrowUpRight size={16} data-arrow="forward" />}
            </button>
          </div>
        </div>
      </section>

      <section className="mt-16 border-t border-zinc-200 pt-6 text-xs font-mono text-zinc-500">
        © {new Date().getFullYear()} Symposia · Questions about sponsorship? <a href="mailto:hello@conferencely.org" className="underline">hello@conferencely.org</a>
      </section>
    </div>
  );
}
